/**
 * Build the mobile drawer's nav menu **based on the user's role**.
 *
 * Each section now declares:
 *   - title        : visible header (string)
 *   - collapsible  : when true, the section is shown as an accordion
 *                    (closed by default unless `defaultOpen` is set)
 *   - defaultOpen  : open by default (e.g. user's primary role section)
 *   - icon         : header icon
 *   - items        : [{ path, icon, label }]
 *
 * The first section (Découverte) is always flat & open.
 *
 * Role-based logic:
 *   • Tous       : Découverte, Préférences
 *   • Connectés  : Mon espace
 *   • Parent     : Espace parent
 *   • Étudiant   : Mon apprentissage (devoirs, progrès, signets)
 *   • Professeur : Mes classes & devoirs (création/suivi)
 *   • Madrassa-Admin : Gestion madrassa
 *   • Admin global : Administration (CRUD + outils)
 */
import {
  Home, BookOpen, GraduationCap, ScrollText, Scroll, History, Users,
  Brain, FileText, Activity, Bookmark, Download,
  Heart, School, Building2, ShieldCheck, UserCog, MailCheck, ListChecks,
  Settings as SettingsIcon, Sun, Mic, HandCoins, Compass, Plus,
  UserRound, BookText, GraduationCap as GradCap, Star, Briefcase,
  Clock, Sun as SunIcon,
} from 'lucide-react';

export function buildDrawerSections({ user, memberships = [] }) {
  const sections = [];
  const isAdminMembership = memberships.some(
    (m) => m.role === 'admin' && m.status === 'approved',
  );
  const isTeacherMembership = memberships.some(
    (m) => m.role === 'teacher' && m.status === 'approved',
  );
  const isParent = user?.signup_role === 'parent';
  const isStudent = !!user && !isParent && !isTeacherMembership
                       && !isAdminMembership && user?.role !== 'admin';

  /* ── Accueil (toujours visible, plat — 1 item) ───────────────────── */
  sections.push({
    title: null,
    icon: Compass,
    collapsible: false,
    items: [
      { path: '/', icon: Home, label: 'Accueil' },
    ],
  });

  /* ── Adoration (Coran, mémorisation, prières, dons) ──────────────── */
  sections.push({
    title: 'Adoration',
    icon: SunIcon,
    collapsible: true,
    defaultOpen: true,
    items: [
      { path: '/quran',            icon: BookOpen, label: 'Lire le Coran' },
      { path: '/memorization',     icon: Brain,    label: 'Mémorisation' },
      { path: '/prieres/horaires', icon: Clock,    label: 'Horaires de prière' },
      { path: '/prieres/qibla',    icon: Compass,  label: 'Qibla' },
      { path: '/dons',             icon: HandCoins, label: 'Faire un don' },
    ],
  });

  /* ── Apprentissage (cours + devoirs selon rôle) ──────────────────── */
  {
    const items = [
      { path: '/cours', icon: GraduationCap, label: 'Cours' },
    ];
    if (user) {
      items.push({ path: '/devoirs?tab=received', icon: FileText, label: 'Devoirs reçus' });
      if (isTeacherMembership || user?.role === 'admin') {
        items.push(
          { path: '/devoirs?tab=sent',   icon: Mic,  label: 'Devoirs envoyés / Récitations' },
          { path: '/devoirs?tab=create', icon: Plus, label: 'Créer un devoir' },
        );
      }
    }
    sections.push({
      title: 'Apprentissage',
      icon: Star,
      collapsible: true,
      defaultOpen: false,
      items,
    });
  }

  /* ── Connaissance (hadith, sira, prophètes, compagnons) ──────────── */
  sections.push({
    title: 'Connaissance',
    icon: BookText,
    collapsible: true,
    defaultOpen: false,
    items: [
      { path: '/hadith',             icon: ScrollText, label: 'Hadiths' },
      { path: '/prophets',           icon: Scroll,     label: 'Prophètes' },
      { path: '/prophet-timeline',   icon: History,    label: 'Muhammad ﷺ — Sīra' },
      { path: '/companions',         icon: Users,      label: 'Compagnons' },
      { path: '/companions/stories', icon: BookText,   label: 'Histoires des Compagnons' },
    ],
  });

  /* ── Mon compte (loggué) ─────────────────────────────────────────── */
  if (user) {
    sections.push({
      title: 'Mon compte',
      icon: UserRound,
      collapsible: true,
      defaultOpen: false,
      items: [
        { path: '/mes-progres',    icon: Activity, label: 'Mes progrès' },
        { path: '/bookmarks',      icon: Bookmark, label: 'Signets' },
        { path: '/settings/audio', icon: Download, label: 'Téléchargements audio' },
      ],
    });
  }

  /* ── Parent ──────────────────────────────────────────────────────── */
  if (isParent) {
    sections.push({
      title: 'Espace parent',
      icon: Heart,
      collapsible: true,
      defaultOpen: true,
      items: [
        { path: '/parent', icon: Heart, label: 'Mes enfants & crédits' },
      ],
    });
  }

  /* ── Professeur (sous-menu) ──────────────────────────────────────── */
  if (isTeacherMembership) {
    sections.push({
      title: 'Espace professeur',
      icon: GradCap,
      collapsible: true,
      defaultOpen: false,
      items: [
        { path: '/teacher/classes', icon: ListChecks, label: 'Mes classes' },
      ],
    });
  }

  /* ── Madrassa admin ──────────────────────────────────────────────── */
  if (isAdminMembership || isTeacherMembership || user?.role === 'admin') {
    const items = [
      { path: '/madrassa', icon: School, label: 'Tableau de bord madrassa' },
    ];
    if (isAdminMembership || user?.role === 'admin') {
      items.push({
        path: '/madrassa-admin/teacher-invitations',
        icon: MailCheck,
        label: 'Inviter des professeurs',
      });
    }
    sections.push({
      title: 'Gestion madrassa',
      icon: Building2,
      collapsible: true,
      defaultOpen: isAdminMembership && !isTeacherMembership,
      items,
    });
  }

  /* ── Administration globale ──────────────────────────────────────── */
  if (user?.role === 'admin') {
    sections.push({
      title: 'Administration',
      icon: ShieldCheck,
      collapsible: true,
      defaultOpen: false,
      items: [
        { path: '/admin',                    icon: ShieldCheck, label: 'Tableau de bord admin' },
        { path: '/admin/prophets',           icon: Scroll,      label: 'Prophètes (CRUD)' },
        { path: '/admin/quests',             icon: HandCoins,   label: 'Quêtes (dons)' },
        { path: '/admin/madrassas',          icon: Building2,   label: 'Toutes les madrassas' },
        { path: '/admin/madrassa-requests',  icon: MailCheck,   label: 'Demandes en attente' },
        { path: '/admin/users',              icon: UserCog,     label: 'Utilisateurs' },
        { path: '/admin/links',              icon: Bookmark,    label: 'Liens admin' },
      ],
    });
  }

  /* ── Préférences (toujours en bas) ───────────────────────────────── */
  sections.push({
    title: 'Préférences',
    icon: Briefcase,
    collapsible: true,
    defaultOpen: false,
    items: [
      { path: '/settings',         icon: SettingsIcon, label: 'Réglages du compte' },
      { path: '/settings/display', icon: Sun,          label: 'Affichage & langue' },
    ],
  });

  return sections;
}
